// Pure, dependency-free helpers for the sliding-session logic used by
// useInactivityTimeout. Kept free of React/axios imports so the exact decision
// logic that runs in the browser can also be exercised directly by smoke tests.

// Proactively refresh the access token once it has less than this long left, so
// an ACTIVE user's session is renewed before the fixed token lifetime expires.
export const REFRESH_MARGIN_MS = 5 * 60_000

/** Read a JWT's `exp` claim (in ms since epoch) without a crypto library. */
export function getTokenExpiryMs(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    let b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    while (b64.length % 4) b64 += '=' // restore base64url padding atob() needs
    const json = JSON.parse(atob(b64)) // atob is global in browsers and Node 18+
    return typeof json.exp === 'number' ? json.exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * Pure decision: should an active user's access token be proactively refreshed
 * now? True when the token is unreadable or within REFRESH_MARGIN_MS of expiry.
 * Throttling / in-flight dedup live in the hook; this predicate is deterministic
 * so it can be smoke-tested against real tokens.
 */
export function shouldProactivelyRefresh(token: string | null, nowMs: number): boolean {
  if (!token) return false
  const expMs = getTokenExpiryMs(token)
  if (expMs === null) return true // can't read expiry → refresh to be safe
  return expMs - nowMs <= REFRESH_MARGIN_MS
}
